import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';

import { eventBus } from '@/configs/eventBus';
import i18n from '@/plugins/i18n';

export type ContentMode = 'standard' | 'split' | 'external';

/**
 * Get root entity from current URL path
 * /operatingcostinvoices/123 → operatingcostinvoices
 * /customers → customers
 */
function getRootEntity(): string {
  const path = window?.location?.pathname || '';
  const segments = path.split('/').filter(Boolean);
  return segments[0] || 'default';
}

/**
 * Migration helper for old localStorage format
 */
function migrateFromOldFormat(): Record<string, ContentMode> {
  const rawData = localStorage.getItem('contentModeByRoute');
  if (!rawData) {
    const oldFormat = localStorage.getItem('contentMode');
    if (oldFormat !== null) {
      try {
        const value = JSON.parse(oldFormat);
        if (typeof value === 'string') {
          const migrated = { default: value as ContentMode };
          localStorage.setItem('contentModeByRoute', JSON.stringify(migrated));
          localStorage.removeItem('contentMode');
          return migrated;
        }
      } catch (_e) {
        //
      }
    }
    return {};
  }

  try {
    const parsed = JSON.parse(rawData);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
  } catch (_e) {
    //
  }
  return {};
}

/**
 * External window state: position, size, and open status
 */
interface ExternalWindowState {
  isOpen: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ContentModeState {
  contentModeByRoute: Record<string, ContentMode>;
  defaultContentMode: ContentMode;
  activeContentModeKey: string; // Tracks which key is currently active (for nested views)
  externalWindowState: Record<string, ExternalWindowState>;
}

/**
 * Content Mode Store
 *
 * Persists which view mode (standard, split, external) is selected per route/entity.
 * Example: { "tickets_123": "split", "operatingcostinvoices_456": "external" }
 */
export const useContentModeStore = defineStore('contentMode', {
  state: (): ContentModeState => ({
    /**
     * Map of route keys to their content mode preference
     * Example: { "tickets_123": "split", "operatingcostinvoices_456": "standard" }
     */
    contentModeByRoute: migrateFromOldFormat(),
    /**
     * Default mode when no preference is set
     */
    defaultContentMode: 'standard',
    /**
     * Currently active content mode key (can be root entity or nested component key)
     * Updated when nested ContentModeWrapper mounts/unmounts
     * Example: "tickets" (root) → "nebenkostenabrechnung" (nested) → "tickets" (unmount)
     */
    activeContentModeKey: getRootEntity(),
    /**
     * External window state per entity
     * Example: { "tickets": { isOpen: true, x: 100, y: 200, width: 600, height: 400 } }
     */
    externalWindowState: {},
  }),

  getters: {
    /**
     * Get content mode for a specific route
     * Returns the stored preference or the default mode if not found
     */
    getContentModeForRoute:
      (state) =>
      (routeKey: string): ContentMode => {
        return state.contentModeByRoute[routeKey] ?? state.defaultContentMode;
      },

    /**
     * Check if split view is currently active
     */
    isSplitViewActive(state): boolean {
      const mode = state.contentModeByRoute[state.activeContentModeKey];
      return mode === 'split';
    },

    /**
     * Get current content mode for the active key
     */
    currentContentMode(state): ContentMode {
      return state.contentModeByRoute[state.activeContentModeKey] ?? state.defaultContentMode;
    },

    /**
     * Get the content mode entity key (the main component or the component)
     */
    contentModeEntityKey: () => {
      const route = useRoute();
      return computed(() => {
        const mainComponentParam = route.params.mainComponent;
        const mainComponent = Array.isArray(mainComponentParam)
          ? mainComponentParam[0]
          : mainComponentParam;

        if (typeof mainComponent === 'string' && mainComponent.length > 0) {
          return mainComponent;
        }

        const componentParam = route.params.component;
        const component = Array.isArray(componentParam) ? componentParam[0] : componentParam;

        if (typeof component === 'string' && component.length > 0) {
          return component;
        }

        return 'default';
      });
    },
  },

  actions: {
    emitViewModeMessage(mode: ContentMode) {
      if (mode !== 'split' && mode !== 'standard') {
        return;
      }

      eventBus.emit('message', {
        summary: i18n.global.t('messages.viewmode'),
        life: 500,
        message:
          mode === 'split'
            ? i18n.global.t('messages.toggledSplitView')
            : i18n.global.t('messages.toggledFullscreenView'),
      });
    },

    /**
     * Set content mode for a specific route and persist to localStorage
     */
    setContentModeForRoute(routeKey: string, mode: ContentMode) {
      this.contentModeByRoute[routeKey] = mode;
      this.persistToLocalStorage();
    },

    /**
     * Reset a route's preference to default
     */
    resetRoutePreference(routeKey: string) {
      delete this.contentModeByRoute[routeKey];
      this.persistToLocalStorage();
    },

    /**
     * Set the global default content mode
     */
    setDefaultContentMode(mode: ContentMode) {
      this.defaultContentMode = mode;
      this.persistToLocalStorage();
    },

    /**
     * Persist current state to localStorage
     */
    persistToLocalStorage() {
      try {
        localStorage.setItem('contentModeByRoute', JSON.stringify(this.contentModeByRoute));
        localStorage.setItem('externalWindowState', JSON.stringify(this.externalWindowState));
      } catch (e) {
        console.error('[contentMode] Failed to persist to localStorage:', e);
      }
    },

    /**
     * Load state from localStorage (called on store initialization)
     */
    loadFromLocalStorage() {
      const stored = migrateFromOldFormat();
      Object.assign(this.contentModeByRoute, stored);

      // Load external window state
      const windowStateStr = localStorage.getItem('externalWindowState');
      if (windowStateStr) {
        try {
          const windowState = JSON.parse(windowStateStr) as Record<string, ExternalWindowState>;
          this.externalWindowState = windowState;
        } catch (e) {
          console.warn('[contentMode] Failed to load window state:', e);
        }
      }
    },

    /**
     * Clear all preferences
     */
    clearAll() {
      this.contentModeByRoute = {};
      this.defaultContentMode = 'standard';
      this.externalWindowState = {};
      this.persistToLocalStorage();
    },

    /**
     * Get external window state for an entity
     */
    getExternalWindowState(entityKey: string): ExternalWindowState {
      return (
        this.externalWindowState[entityKey] || {
          isOpen: false,
          x: 100,
          y: 100,
          width: 600,
          height: 400,
        }
      );
    },

    /**
     * Update external window state (position, size, open status)
     */
    setExternalWindowState(entityKey: string, state: ExternalWindowState) {
      this.externalWindowState[entityKey] = state;
      this.persistToLocalStorage();
    },

    /**
     * Toggle external window visibility
     */
    toggleExternalWindow(entityKey: string) {
      const current = this.getExternalWindowState(entityKey);
      current.isOpen = !current.isOpen;
      this.setExternalWindowState(entityKey, current);
    },

    /**
     * Close external window for entity
     */
    closeExternalWindow(entityKey: string) {
      const current = this.getExternalWindowState(entityKey);
      current.isOpen = false;
      this.setExternalWindowState(entityKey, current);
    },

    /**
     * Toggle between standard and split view for current route
     * Used by UI toggle buttons - only toggles standard/split, never external
     */
    toggleViewMode() {
      const currentMode =
        this.contentModeByRoute[this.activeContentModeKey] ?? this.defaultContentMode;

      // Toggle: standard ↔ split
      const newMode: ContentMode = currentMode === 'standard' ? 'split' : 'standard';
      this.setContentModeForRoute(this.activeContentModeKey, newMode);
      this.emitViewModeMessage(newMode);
    },

    toggleViewModeForRoute(routeKey: string): ContentMode {
      const currentMode = this.contentModeByRoute[routeKey] ?? this.defaultContentMode;
      const newMode: ContentMode = currentMode === 'standard' ? 'split' : 'standard';

      this.setContentModeForRoute(routeKey, newMode);
      this.emitViewModeMessage(newMode);

      return newMode;
    },

    /**
     * Set the currently active content mode key (called by nested ContentModeWrapper)
     * This allows nested components to override the root entity's settings
     */
    setActiveContentModeKey(key: string) {
      if (this.activeContentModeKey !== key) {
        this.activeContentModeKey = key;
      }
    },
  },
});
